"use client";

import { motion, useReducedMotion } from "motion/react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { appStoreUrl } from "@/lib/constants";
import { cn } from "@/lib/utils";

const FLOATING_CARDS = [
  {
    label: "💍 The big day",
    src: "/images/hero/cards/big-day.jpg",
    position: "left-[5%] top-[5%]",
    visibility: "hidden md:block",
    rotate: -4,
    loop: { duration: 4.5, delay: 1.2 },
    entrance: { delay: 0.6 },
  },
  {
    label: "🎉 Festival night",
    src: "/images/hero/cards/festival-night.jpg",
    position: "right-[4%] top-[4%]",
    visibility: "hidden md:block",
    rotate: 3,
    loop: { duration: 5, delay: 1.4 },
    entrance: { delay: 0.75 },
  },
  {
    label: "🌙 After dark",
    src: "/images/hero/cards/after-dark.jpg",
    position: "left-[2%] top-[42%]",
    visibility: "hidden xl:block",
    rotate: 3,
    loop: { duration: 5.5, delay: 1.6 },
    entrance: { delay: 0.9 },
  },
  {
    label: "🌅 Golden hour",
    src: "/images/hero/cards/golden-hour.jpg",
    position: "right-[1.5%] top-[40%]",
    visibility: "hidden xl:block",
    rotate: -3,
    loop: { duration: 4.8, delay: 1.8 },
    entrance: { delay: 1.05 },
  },
  {
    label: "🏖️ Beach day",
    src: "/images/hero/cards/beach-day.jpg",
    position: "left-[11%] bottom-[3%]",
    visibility: "hidden md:block",
    rotate: 4,
    loop: { duration: 5.2, delay: 2 },
    entrance: { delay: 1.2 },
  },
  {
    label: "🌆 City lights",
    src: "/images/hero/cards/city-lights.jpg",
    position: "right-[11%] bottom-[4%]",
    visibility: "hidden md:block",
    rotate: -3,
    loop: { duration: 4.6, delay: 2.2 },
    entrance: { delay: 1.35 },
  },
];

export function Hero() {
  const reduced = useReducedMotion();

  return (
    <section className="relative flex min-h-svh flex-col justify-center overflow-hidden pt-16 pb-12 md:pt-[88px]">
      {/* Floating photo cards — layer starts below the fixed nav */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-16 bottom-0 md:top-[88px]"
      >
        {FLOATING_CARDS.map((card) => (
          <motion.div
            key={card.label}
            className={cn("absolute", card.position, card.visibility)}
            initial={
              reduced ? { opacity: 0 } : { opacity: 0, y: 48, scale: 0.85 }
            }
            animate={
              reduced
                ? { opacity: 1, rotate: card.rotate }
                : {
                    opacity: 1,
                    scale: 1,
                    y: [0, -10, 0],
                    rotate: [card.rotate, card.rotate + 2, card.rotate],
                  }
            }
            transition={
              reduced
                ? { duration: 0.6, delay: card.entrance.delay }
                : {
                    opacity: { duration: 0.6, delay: card.entrance.delay },
                    scale: {
                      duration: 0.8,
                      delay: card.entrance.delay,
                      ease: "backOut",
                    },
                    y: {
                      duration: card.loop.duration,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: card.loop.delay,
                    },
                    rotate: {
                      duration: card.loop.duration,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: card.loop.delay,
                    },
                  }
            }
          >
            <div className="relative w-[170px] xl:w-[195px]">
              <Image
                src={card.src}
                alt=""
                width={390}
                height={296}
                className="aspect-[195/148] w-full rounded-3xl object-cover"
              />
              <span className="bg-secondary text-secondary-foreground absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-1.5 text-sm whitespace-nowrap">
                {card.label}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="relative z-10 container flex flex-col items-center text-center"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {
            transition: { staggerChildren: 0.12, delayChildren: 0.1 },
          },
        }}
      >
        {/* Headline */}
        <motion.h1
          className="text-foreground text-6xl font-bold tracking-[0.01em] md:text-7xl lg:text-[6rem]"
          variants={{
            // Transform-only entrance: the H1 is the mobile LCP element, so it must
            // paint (opaque, unblurred) in the SSR HTML rather than wait on hydration.
            hidden: { y: 24 },
            visible: {
              y: 0,
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          <span className="sr-only">
            A shared camera app for weddings, parties, and trips
          </span>
          <span aria-hidden="true">Keeps</span>
        </motion.h1>

        {/* Subhead */}
        <motion.p
          className="font-display text-foreground mt-6 text-2xl font-normal tracking-[0.01em] md:text-3xl lg:text-4xl"
          variants={{
            // Transform-only so the subhead (LCP candidate) paints immediately.
            hidden: { y: 20 },
            visible: {
              y: 0,
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          capture moments together
        </motion.p>

        {/* Subcopy + CTAs */}
        <motion.div
          className="flex flex-col items-center"
          variants={{
            hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
            visible: {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          <p className="text-muted-foreground mt-8 max-w-[520px] text-base leading-6">
            A premium shared camera for weddings, parties, and trips.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:gap-7">
            <Button asChild className="h-14 rounded-xl px-7 text-[17px]">
              <Link
                href={appStoreUrl("web_home_hero")}
                target="_blank"
                rel="noopener"
              >
                Create your event for free
              </Link>
            </Button>
            <Link
              href="#how-it-works"
              scroll={false}
              className="group/link text-foreground text-[15px] font-medium no-underline"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("how-it-works")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              See how it works{" "}
              <span className="inline-block transition-transform duration-200 group-hover/link:translate-x-0.5">
                →
              </span>
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
